import React, { useMemo, useCallback, useState } from "react";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  List,
  ListItem,
  Avatar,
  TextField,
  IconButton,
  Grid,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Paper,
  Divider,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  ShoppingCart as CartIcon,
  Group as GroupIcon,
  Payment as PaymentIcon,
  Remove as RemoveIcon,
  Add as AddIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Loyalty as VoucherIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { searchUserByEmail, createUser } from "../../api/users";

// ✅ footer bạn đã làm nhỏ -> không cần 350 nữa
const FOOTER_HEIGHT = 240;

export default function CartTabs({
  tabValue,
  setTabValue,
  products,
  customer,
  setCustomer,
  note,
  setNote,
  subtotal,
  handleQuantityChange,
  paymentMethod,
  setPaymentMethod,
}) {
  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down("sm"));

  const totalLines = products.length;
  const totalQty = useMemo(
    () => products.reduce((s, it) => s + (it.quantity || 0), 0),
    [products]
  );

  const [searching, setSearching] = useState(false);

  const handleSearchCustomer = useCallback(async () => {
    if (!customer.searchKey || searching) return;

    try {
      setSearching(true);
      const result = await searchUserByEmail(customer.searchKey);

      if (result?.error) {
        alert(result.error);
        return;
      }

      let user = null;
      if (result?.exists) {
        user = result.data;
      } else {
        const username = customer.searchKey.includes("@")
          ? customer.searchKey.split("@")[0]
          : customer.searchKey;

        const newUser = {
          username,
          email: customer.searchKey.includes("@") ? customer.searchKey : "",
          phone: customer.phone || "",
          address: customer.address || "",
          dateOfBirth: null,
          gender: 0,
        };

        user = await createUser(newUser);
        alert("✅ Tạo user mới thành công!");
      }

      if (user) {
        setCustomer({
          ...customer,
          userId: user.userId,
          name: user.username || "",
          phone: user.phone || "",
          email: user.email || "",
          address: user.address || "",
          loyaltyPoints: user.pointAccumulated || 0,
        });
      }
    } catch (err) {
      console.error("❌ Lỗi xử lý khách hàng:", err);
      alert("Có lỗi khi tìm/tạo khách hàng!");
    } finally {
      setSearching(false);
    }
  }, [customer, setCustomer, searching]);

  const headerBg = theme.palette.background.paper;

  const TabHeader = ({ icon, title, right }) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.75, // ✅ nhỏ hơn
        mb: 0.75,  // ✅ nhỏ hơn
      }}
    >
      {icon}
      <Typography variant="subtitle1" fontWeight={900}>
        {title}
      </Typography>
      <Box sx={{ ml: "auto" }}>{right}</Box>
    </Box>
  );

  const QtyControl = ({ p }) => {
    const max = Math.max(Number(p.stock || 0), 1);
    const qty = Number(p.quantity || 1);

    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
        <Tooltip title="Giảm">
          <span>
            <IconButton
              size="small"
              onClick={() => handleQuantityChange(p.key, qty - 1)}
              sx={{ borderRadius: 2 }}
            >
              <RemoveIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <TextField
          value={qty}
          onChange={(e) => {
            const v = Number(e.target.value || 0);
            if (Number.isNaN(v)) return;
            handleQuantityChange(p.key, v);
          }}
          type="number"
          size="small"
          inputProps={{
            min: 1,
            max,
            step: 1,
            style: { textAlign: "center", width: smDown ? 52 : 60 },
          }}
          sx={{
            mx: 0.25,
            "& .MuiOutlinedInput-root": { borderRadius: 2 },
          }}
        />

        <Tooltip title="Tăng">
          <span>
            <IconButton
              size="small"
              onClick={() => handleQuantityChange(p.key, qty + 1)}
              disabled={qty >= max}
              sx={{ borderRadius: 2 }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      {/* Tabs sticky */}
      <Box
        sx={{
          borderBottom: `1px solid ${theme.palette.divider}`,
          mb: 0.5,
          position: "sticky",
          top: 0,
          zIndex: 5,
          bgcolor: headerBg,
        }}
      >
        <Tabs
          value={tabValue}
          onChange={(_e, v) => setTabValue(v)}
          variant="scrollable"
          allowScrollButtonsMobile
          sx={{
            minHeight: 34,
            "& .MuiTab-root": {
              minHeight: 34,
              padding: "6px 10px",
              fontSize: "0.85rem",
            },
          }}
        >
          <Tab icon={<CartIcon />} iconPosition="start" label="Giỏ hàng" />
          <Tab icon={<GroupIcon />} iconPosition="start" label="Khách hàng" />
          <Tab icon={<PaymentIcon />} iconPosition="start" label="Thanh toán" />
        </Tabs>
      </Box>

      {/* ✅ ONLY ONE scroll here */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          pb: `${FOOTER_HEIGHT + 12}px`, // ✅ ít hơn + đủ chỗ footer
        }}
      >
        {/* TAB GIỎ HÀNG */}
        {tabValue === 0 && (
          <Box sx={{ p: { xs: 1, md: 1.25 } }}>
            <TabHeader
              icon={<CartIcon color="primary" />}
              title="Giỏ hàng"
              right={
                <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", justifyContent: "flex-end" }}>
                  <Chip size="small" label={`Dòng: ${totalLines}`} />
                  <Chip size="small" label={`SL: ${totalQty}`} />
                  <Chip
                    size="small"
                    color="primary"
                    variant="outlined"
                    label={`Tạm tính: ${subtotal.toLocaleString("vi-VN")}₫`}
                  />
                </Box>
              }
            />

            {products.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 5 }}>
                <CartIcon sx={{ fontSize: 44, color: "text.secondary", mb: 1.25 }} />
                <Typography fontWeight={900}>Giỏ hàng đang trống</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Hãy chọn sản phẩm bên trái và bấm thêm vào giỏ.
                </Typography>
              </Box>
            ) : (
              <List disablePadding sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {products.map((p) => {
                  const qty = Number(p.quantity || 1);
                  return (
                    <Paper key={p.key} variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
                      <ListItem
                        sx={{
                          py: 1,
                          px: 1,
                          gap: 1,
                          alignItems: "stretch",
                          flexDirection: { xs: "column", sm: "row" },
                        }}
                      >
                        <Box sx={{ display: "flex", gap: 1, flex: 1, minWidth: 0 }}>
                          <Avatar
                            src={p.image}
                            variant="rounded"
                            sx={{
                              width: { xs: 52, sm: 60 },
                              height: { xs: 52, sm: 60 },
                              borderRadius: 2,
                              flexShrink: 0,
                            }}
                          >
                            {p.name?.charAt(0)}
                          </Avatar>

                          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Typography fontWeight={900} noWrap>
                              {p.name}
                            </Typography>

                            <Typography variant="caption" color="text.secondary" noWrap>
                              {p.sku ? `SKU: ${p.sku}` : "SKU: -"} •{" "}
                              {p.price.toLocaleString("vi-VN")}₫ / 1
                            </Typography>

                            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.75 }}>
                              {p.color && <Chip size="small" label={`Màu: ${p.color}`} />}
                              {p.size && <Chip size="small" label={`Size: ${p.size}`} />}
                              <Chip
                                size="small"
                                variant="outlined"
                                color={(p.stock ?? 0) > 0 ? "success" : "default"}
                                label={`Tồn: ${p.stock ?? 0}`}
                              />
                            </Box>
                          </Box>
                        </Box>

                        <Divider
                          flexItem
                          orientation={smDown ? "horizontal" : "vertical"}
                          sx={{ my: smDown ? 0.5 : 0 }}
                        />

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1,
                            width: { xs: "100%", sm: 300 },
                          }}
                        >
                          <QtyControl p={p} />

                          <Box sx={{ textAlign: "right", minWidth: 110 }}>
                            <Typography variant="caption" color="text.secondary">
                              Thành tiền
                            </Typography>
                            <Typography fontWeight={900} sx={{ fontSize: "0.98rem" }}>
                              {(p.price * qty).toLocaleString("vi-VN")}₫
                            </Typography>
                          </Box>

                          <Tooltip title="Xoá dòng">
                            <IconButton
                              color="error"
                              onClick={() => handleQuantityChange(p.key, 0)}
                              sx={{ borderRadius: 2 }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </ListItem>
                    </Paper>
                  );
                })}
              </List>
            )}
          </Box>
        )}

        {/* TAB KHÁCH HÀNG */}
        {tabValue === 1 && (
          <Box sx={{ p: { xs: 1, md: 1.25 } }}>
            <TabHeader icon={<GroupIcon color="primary" />} title="Thông tin khách hàng" />

            <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Tìm theo email / số điện thoại (Enter hoặc icon tìm)
              </Typography>

              <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                <TextField
                  fullWidth
                  placeholder="Nhập số điện thoại hoặc email"
                  value={customer.searchKey || ""}
                  onChange={(e) => setCustomer({ ...customer, searchKey: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSearchCustomer();
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: customer.searchKey ? (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setCustomer({ ...customer, searchKey: "" })}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />

                <Tooltip title={searching ? "Đang tìm..." : "Tìm / tạo khách"}>
                  <span>
                    <IconButton
                      color="primary"
                      onClick={handleSearchCustomer}
                      disabled={searching || !customer.searchKey}
                      sx={{
                        borderRadius: 2,
                        border: (t) => `1px solid ${t.palette.divider}`,
                        width: 44,
                      }}
                    >
                      <SearchIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>

              <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
                {!!customer.userId && <Chip label={`UserID: ${customer.userId}`} size="small" />}
                <Chip label={`Điểm: ${customer.loyaltyPoints || 0}`} size="small" variant="outlined" />
              </Box>
            </Paper>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Họ và tên"
                  value={customer.name || ""}
                  onChange={(e) => setCustomer({ ...customer, name: e.target.value })}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Số điện thoại"
                  value={customer.phone || ""}
                  onChange={(e) => setCustomer({ ...customer, phone: e.target.value })}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  value={customer.email || ""}
                  onChange={(e) => setCustomer({ ...customer, email: e.target.value })}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Địa chỉ"
                  value={customer.address || ""}
                  multiline
                  rows={2}
                  onChange={(e) => setCustomer({ ...customer, address: e.target.value })}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Điểm tích lũy"
                  value={customer.loyaltyPoints || 0}
                  onChange={(e) => setCustomer({ ...customer, loyaltyPoints: e.target.value })}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <VoucherIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* TAB THANH TOÁN */}
        {tabValue === 2 && (
          <Box sx={{ p: { xs: 1, md: 1.25 } }}>
            <TabHeader icon={<PaymentIcon color="primary" />} title="Thanh toán" />

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Phương thức thanh toán</InputLabel>
                  <Select
                    value={paymentMethod}
                    label="Phương thức thanh toán"
                    onChange={(e) => setPaymentMethod(e.target.value)}
                  >
                    <MenuItem value="cash">Tiền mặt</MenuItem>
                    <MenuItem value="transfer">Chuyển khoản</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Ghi chú đơn hàng"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  multiline
                  rows={3}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Grid>

              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Tóm tắt
                  </Typography>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.75 }}>
                    <Typography color="text.secondary">Dòng / SL</Typography>
                    <Typography fontWeight={900}>
                      {totalLines} / {totalQty}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
                    <Typography color="text.secondary">Tạm tính</Typography>
                    <Typography fontWeight={900}>
                      {subtotal.toLocaleString("vi-VN")}₫
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>
    </Box>
  );
}
